// UpdatePaymentPage, lets users with failed payments go to Stripe Customer Portal
// to update their payment method, or directly manage their subscription.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, AlertTriangle, ArrowLeft, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

export function UpdatePaymentPage() {
  const { profile }         = useAuth();
  const navigate            = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  const isDunning = profile?.payment_failed === true;

  async function handleOpenPortal() {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-portal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token ?? ''}`,
          },
          body: JSON.stringify({ returnUrl: `${window.location.origin}/configuracoes/pagamento` }),
        },
      );
      const json = await res.json();
      const url = json?.data?.url as string | undefined;
      if (url) {
        window.location.href = url;
      } else {
        setError('Não foi possível abrir o portal de pagamento. Tente novamente.');
      }
    } catch {
      setError('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page-container" style={{ maxWidth: '600px' }}>

      <button
        className="btn btn-ghost"
        onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', padding: '0.5rem 0' }}
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      {isDunning && (
        <div style={{
          display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
          backgroundColor: 'rgba(229, 62, 62, 0.08)',
          border: '1px solid rgba(229, 62, 62, 0.3)',
          borderRadius: 'var(--radius-card)',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
        }}>
          <AlertTriangle size={20} color="#E53E3E" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ fontWeight: 700, color: '#E53E3E', margin: '0 0 0.25rem', fontSize: 'var(--text-label)' }}>
              Pagamento pendente
            </p>
            <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: 'var(--text-caption)', lineHeight: 1.5 }}>
              Não conseguimos processar o pagamento da sua assinatura. Atualize seu método de
              pagamento para manter o acesso ao Professya.
            </p>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <CreditCard size={24} color="var(--petrol)" />
          <h1 style={{ fontSize: 'var(--text-title)', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            Método de pagamento
          </h1>
        </div>

        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 1.5rem', fontSize: 'var(--text-body)' }}>
          Gerencie seu cartão de crédito, veja o histórico de cobranças e atualize
          suas informações de pagamento no portal seguro do Stripe.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {[
            'Atualizar cartão de crédito ou débito',
            'Ver histórico de faturas e cobranças',
            'Alterar plano ou cancelar assinatura',
            'Baixar recibos de pagamento',
          ].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: 'var(--petrol)', fontWeight: 700 }}>✦</span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>{item}</span>
            </div>
          ))}
        </div>

        {error && (
          <p style={{ color: '#E53E3E', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>{error}</p>
        )}

        <button
          className="btn btn-primary"
          onClick={handleOpenPortal}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'center' }}
        >
          {loading ? 'Abrindo portal...' : (
            <>
              <ExternalLink size={16} />
              Gerenciar pagamento
            </>
          )}
        </button>

        <p style={{ color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center', marginTop: '0.75rem' }}>
          Você será redirecionado para o portal seguro do Stripe
        </p>
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', margin: 0 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Precisa de ajuda?</strong>{' '}
          Entre em contato com nossa equipe em{' '}
          <a href="mailto:contato@professya.com" style={{ color: 'var(--petrol)' }}>
            contato@professya.com
          </a>
        </p>
      </div>
    </div>
  );
}
